import React from 'react';
import { CheckoutConfig, TestimonialConfig } from '../../../types';

interface TrustTabProps {
  config: CheckoutConfig;
  onUpdate: (partial: Partial<CheckoutConfig>) => void;
}

export const CheckoutStudioTrustTab: React.FC<TrustTabProps> = ({ config, onUpdate }) => {
  const testimonial = config.testimonial || { quote: '', author: '', role: '', avatarUrl: '' };

  const handleTestimonialChange = (key: keyof TestimonialConfig, val: string) => {
    onUpdate({ testimonial: { ...testimonial, [key]: val } });
  };

  return (
    <div className="space-y-4 text-left">
      {/* Security & Guarantees */}
      <div className="p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-3">
        <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Security Seals & Guarantees</p>

        <label className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">
          <div>
            <p className="text-xs font-bold text-slate-900 dark:text-white">Show 256-Bit SSL Security Badge</p>
            <p className="text-[11px] text-slate-500">Displays PCI-DSS compliance lock icon on checkout.</p>
          </div>
          <input
            type="checkbox"
            checked={Boolean(config.showSecurityBadge)}
            onChange={(e) => onUpdate({ showSecurityBadge: e.target.checked })}
            className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
          />
        </label>

        <div className="space-y-2">
          <label className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">
            <div>
              <p className="text-xs font-bold text-slate-900 dark:text-white">Show Money-Back Guarantee</p>
              <p className="text-[11px] text-slate-500">Highlights risk reversal guarantee seal.</p>
            </div>
            <input
              type="checkbox"
              checked={Boolean(config.showGuarantee)}
              onChange={(e) => onUpdate({ showGuarantee: e.target.checked })}
              className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
            />
          </label>

          {config.showGuarantee && (
            <input
              type="text"
              value={config.guaranteeText || ''}
              onChange={(e) => onUpdate({ guaranteeText: e.target.value })}
              placeholder="e.g., 30-Day 100% Money-Back Guarantee"
              className="w-full text-xs px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
            />
          )}
        </div>
      </div>

      {/* Customer Testimonial Card */}
      <div className="p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-3">
        <label className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">
          <div>
            <p className="text-xs font-bold text-slate-900 dark:text-white">Show Featured Customer Review</p>
            <p className="text-[11px] text-slate-500">Displays 5-star customer review card.</p>
          </div>
          <input
            type="checkbox"
            checked={Boolean(config.showTestimonial)}
            onChange={(e) => onUpdate({ showTestimonial: e.target.checked })}
            className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
          />
        </label>

        {config.showTestimonial && (
          <div className="space-y-2 pt-1">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase">Review Quote</label>
              <textarea
                value={testimonial.quote}
                onChange={(e) => handleTestimonialChange('quote', e.target.value)}
                rows={2}
                placeholder="What did the customer say?"
                className="w-full text-xs p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Author Name</label>
                <input
                  type="text"
                  value={testimonial.author}
                  onChange={(e) => handleTestimonialChange('author', e.target.value)}
                  placeholder="e.g. Alex Morgan"
                  className="w-full text-xs px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase">Role / Company</label>
                <input
                  type="text"
                  value={testimonial.role}
                  onChange={(e) => handleTestimonialChange('role', e.target.value)}
                  placeholder="e.g. Founder at Hyper"
                  className="w-full text-xs px-2.5 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
